import type { Post } from '../types';
import type { EvidenceRung, ReportModel, ReportRow, ReportSection } from './reportModel';
import { RUNG_LABEL } from './reportModel';
import { resolveAfterImage, resolvePair, type ResolvedPair } from './beforeAfter';
import { formatShortDate } from './time';

/**
 * The PDF's own view of the report, built from the same `ReportModel` the
 * on-screen record renders. Kept pure and serializable (no React, no
 * @react-pdf import) so it can be unit tested without a DOM or a PDF
 * renderer, and so `reportPdf.web.ts` can hand it straight to
 * `pdfImages.web.ts` for image prefetching.
 *
 * Images are resolved thumbnail-first (400px) rather than full-size: the
 * printed page never needs more resolution than that, and the smaller
 * fetch matters more here than on screen because every figure has to be
 * pulled down and re-encoded as a data URL before the PDF can render it.
 */

const MAX_FIGURES_PER_ROW = 2;
const MAX_FIGURES_PER_DOCUMENT = 40;

export type PdfFigure =
  | {
      kind: 'pair';
      beforeUrl: string;
      afterUrl: string;
      beforeDate: string | null;
      afterDate: string | null;
      caption?: string;
      byline: string;
    }
  | {
      kind: 'single';
      url: string;
      caption?: string;
      byline: string;
    };

export interface PdfNote {
  caption: string;
  byline: string;
}

export interface PdfRow {
  key: string;
  label: string;
  description?: string;
  rung: EvidenceRung;
  rungLabel: string;
  figures: PdfFigure[];
  notes: PdfNote[];
}

export interface PdfSection {
  key: string;
  label: string;
  detail: string;
  documented: number;
  total: number;
  rows: PdfRow[];
}

export interface PdfCoverage {
  checklistId: string;
  name: string;
  hazard?: string;
  documented: number;
  total: number;
  isActive: boolean;
}

export interface PdfOpenRow {
  label: string;
  rungLabel: string;
}

export interface PdfTimelineEntry {
  label: string;
  date: string | null;
}

export interface PdfCover {
  address: string;
  place: string;
  generatedAt: string;
  modelName: string;
  documentedCount: number;
  documentedTotal: number;
  attestedCount: number;
  attestedTotal: number;
}

export interface PdfLimits {
  body: string;
  openNote: string;
  footer: string;
}

export interface PdfReportSections {
  cover: PdfCover;
  coverage: PdfCoverage[];
  sections: PdfSection[];
  openRows: PdfOpenRow[];
  timeline: PdfTimelineEntry[];
  limits: PdfLimits;
  /** Every figure URL referenced anywhere in the document, in render order,
   *  de-duplicated. This is the fetch list for `pdfImages.web.ts`. */
  imageUrls: string[];
  /** Figures dropped by either cap, so the screen can say some photographs
   *  did not make it into the download. */
  omittedFigureCount: number;
}

// Mirrors src/components/report/EvidenceFigure.tsx's byline() exactly. Not
// imported from there: that file pulls in React and expo-image, and this
// module is meant to stay a plain, DOM-free util.
function byline(post: Post): string {
  const who = post.isAnonymous ? 'Fire Scout' : post.authorName;
  const when = formatShortDate(post.createdAt);
  return when ? `Filed by ${who}, ${when}` : `Filed by ${who}`;
}

function formatGeneratedAt(): string {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function pairFigure(pair: ResolvedPair): PdfFigure {
  // Re-resolve thumbnail-first: reportModel's pairs are built with
  // resolvePair(post, /* full */ true) for the on-screen record, which
  // prefers full-size originals. The PDF wants the smaller thumbnail.
  const thumbPair = resolvePair(pair.post, false) ?? pair;
  return {
    kind: 'pair',
    beforeUrl: thumbPair.beforeUri,
    afterUrl: thumbPair.afterUri,
    beforeDate: formatShortDate(pair.post.beforePostCreatedAt),
    afterDate: formatShortDate(pair.post.createdAt),
    caption: pair.post.caption || undefined,
    byline: byline(pair.post),
  };
}

function singleFigure(post: Post): PdfFigure | null {
  const url = resolveAfterImage(post, false);
  if (!url) return null;
  return {
    kind: 'single',
    url,
    caption: post.caption || undefined,
    byline: byline(post),
  };
}

function figuresForRow(row: ReportRow): { figures: PdfFigure[]; notes: PdfNote[] } {
  const figures: PdfFigure[] = [];
  // Pairs first: the strongest evidence a row can carry earns the two slots
  // before any loose photo does.
  for (const pair of row.pairs) figures.push(pairFigure(pair));

  const loosePhotos = row.posts.filter((p) => !row.consumedByPair.has(p.id) && p.imageUrl);
  for (const post of loosePhotos) {
    const figure = singleFigure(post);
    if (figure) figures.push(figure);
  }

  const notePosts = row.posts.filter(
    (p) => !row.consumedByPair.has(p.id) && !p.imageUrl && p.caption
  );
  const notes: PdfNote[] = notePosts.map((post) => ({
    caption: post.caption,
    byline: byline(post),
  }));

  return { figures, notes };
}

function buildRow(row: ReportRow): { row: PdfRow; omitted: number } {
  const { figures, notes } = figuresForRow(row);
  const kept = figures.slice(0, MAX_FIGURES_PER_ROW);
  const omitted = figures.length - kept.length;
  return {
    row: {
      key: row.key,
      label: row.label,
      description: row.description,
      rung: row.rung,
      rungLabel: RUNG_LABEL[row.rung],
      figures: kept,
      notes,
    },
    omitted,
  };
}

function figureUrl(figure: PdfFigure): string[] {
  return figure.kind === 'pair' ? [figure.beforeUrl, figure.afterUrl] : [figure.url];
}

/** Serializable, print-ready view of a `ReportModel`. Pure: no React, no
 *  network, no @react-pdf import — safe to unit test directly. */
export function toPdfSections(report: ReportModel): PdfReportSections {
  const { structure } = report;
  const place = [structure.city, structure.state].filter(Boolean).join(', ');

  let omittedFigureCount = 0;

  const sections: PdfSection[] = report.sections
    .filter((section: ReportSection) => section.rows.length > 0)
    .map((section: ReportSection) => {
      const rows = section.rows.map((row) => {
        const built = buildRow(row);
        omittedFigureCount += built.omitted;
        return built.row;
      });
      return {
        key: section.key,
        label: section.label,
        detail: section.detail,
        documented: section.documented,
        total: section.rows.length,
        rows,
      };
    });

  // Global cap: enforced in document order (section order, then row order,
  // then figure order within a row), which is also the order every figure
  // is about to be fetched in.
  let seen = 0;
  const imageUrls: string[] = [];
  for (const section of sections) {
    for (const row of section.rows) {
      const kept: PdfFigure[] = [];
      for (const figure of row.figures) {
        if (seen >= MAX_FIGURES_PER_DOCUMENT) {
          omittedFigureCount += 1;
          continue;
        }
        seen += 1;
        kept.push(figure);
        imageUrls.push(...figureUrl(figure));
      }
      row.figures = kept;
    }
  }

  const coverage: PdfCoverage[] = report.coverage.map((c) => ({
    checklistId: c.checklistId,
    name: c.name,
    hazard: c.hazard,
    documented: c.documented,
    total: c.total,
    isActive: c.isActive,
  }));

  const openRows: PdfOpenRow[] = report.openRows.map((row) => ({
    label: row.label,
    rungLabel: RUNG_LABEL[row.rung],
  }));

  const timeline: PdfTimelineEntry[] = report.timeline.map(({ post, label }) => ({
    label: label ?? post.checklistItemLabel ?? 'Observation',
    date: formatShortDate(post.createdAt),
  }));

  const generatedAt = formatGeneratedAt();

  return {
    cover: {
      address: structure.address ?? 'Unknown address',
      place,
      generatedAt,
      modelName: report.modelName,
      documentedCount: report.documentedCount,
      documentedTotal: report.documentedTotal,
      attestedCount: report.attestedCount,
      attestedTotal: report.attestedTotal,
    },
    coverage,
    sections,
    openRows,
    timeline,
    limits: {
      body:
        'This is a record of what has been observed and reported at this property, with the ' +
        'photographs and dates behind each item. Items marked complete without a photograph are ' +
        "the owner's own report. REDI does not inspect properties and does not verify the work, " +
        'and this record does not certify compliance with any building code, fire code, or ' +
        'insurance requirement.',
      openNote:
        "Some conditions sit outside any one owner's control, such as road access, the width " +
        'of the street, and what neighboring parcels do. They are not counted against this ' +
        'property.',
      footer: `Generated by REDI · weareredi.net · ${generatedAt}`,
    },
    imageUrls,
    omittedFigureCount,
  };
}
